import Button from "@mui/material/Button";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import Breadcrumb from "../../../components/Breadcrumb";
import { useBreadcrumb } from "../../../hooks/useBreadcrumb";
import { memberHandler } from "../../../redux/handlers";
import DebtorRecordTab from "./tabs/DebtorRecordTab";
import InvoiceTab from "./tabs/InvoiceTab";
import ReceiptTab from "./tabs/ReceiptTab";

const INITIAL_TAB_STATUS = 1;

const TAB_CONFIG = [
  { id: 1, label: "รายการบันทึกลูกหนี้" },
  { id: 2, label: "ใบแจ้งหนี้" },
  { id: 3, label: "ใบเสร็จ" },
];

export function DebtorCustomerDetail() {
  const { customerID } = useParams();
  const dispatch = useDispatch();

  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabStatus, setTabStatus] = useState(INITIAL_TAB_STATUS);

  const breadcrumbConfig = [
    { label: "รายงาน", path: "/" },
    { label: "รายงานลูกหนี้", path: "/report/debtor/list" },
    { label: memberData?.code || "รายละเอียดลูกค้า" },
  ];
  useBreadcrumb(breadcrumbConfig);

  const fetchMemberData = useCallback(async () => {
    if (!customerID) return;
    
    try {
      setLoading(true);
      const response = await dispatch(memberHandler.getMemberById(customerID));
      setMemberData(response);
      setError(null);
    } catch (err) {
      console.error("Error fetching member data:", err);
      setError("ไม่สามารถโหลดข้อมูลลูกค้าได้");
    } finally {
      setLoading(false);
    }
  }, [dispatch, customerID]);

  useEffect(() => {
    fetchMemberData();
  }, [fetchMemberData]);

  const formatMembershipDuration = useCallback((createdAt) => {
    if (!createdAt) return "-";
    const now = moment();
    const created = moment(createdAt);
    const duration = moment.duration(now.diff(created));
    const years = Math.floor(duration.asYears());
    const months = Math.floor(duration.asMonths() % 12);
    return `${years} ปี ${months} เดือน`;
  }, []);

  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return "0.00";
    return new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const getFullName = useCallback((member) => {
    if (!member) return "-";
    const fullName = `${member.first_name_th || ""} ${member.last_name_th || ""}`.trim();
    return fullName || "-";
  }, []);

  const handleChangeTab = useCallback((status) => {
    setTabStatus(status);
  }, []);

  if (loading) {
    return (
      <div>
        <Breadcrumb />
        <div className="text-center py-8">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Breadcrumb />
        <div className="text-center py-8 text-red-500">{error}</div>
      </div>
    );
  }

  if (!memberData) {
    return (
      <div>
        <Breadcrumb />
        <div className="text-center py-8">ไม่พบข้อมูลลูกค้า</div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb />

      <div className="card card-st-2">
        <div className="card-body px-0 py-4">
          <div className="text-lg font-bold mb-4 lh-1 px-4 mb-4 text-[#2DC76D]">
            รายงานลูกหนี้
          </div>

          <div className="px-4">
            <div className="grid grid-cols-11 gap-7">
              <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
                <div className="font-bold text-[#636578] text-xl mb-3">
                  {getFullName(memberData)}
                </div>
                <div className="flex mb-3">
                  <div className="flex-[0_0_81px] text-end mr-2">
                    รหัสสมาชิก :
                  </div>
                  <div className="text-[#2684FC]">{memberData.code}</div>
                </div>
                <div className="flex">
                  <div className="flex-[0_0_81px] text-end mr-2">
                    เบอร์โทร :
                  </div>
                  <div className="text-[#2684FC]">
                    {memberData.phone_number || "-"}
                  </div>
                </div>
              </div>

              <div className="col-span-3 border-r-[1px] border-[#E5E5E5]">
                <div className="flex mb-3">
                  <div className="flex-[0_0_150px]">ระยะเวลาเป็นสมาชิก</div>
                  <div className="text-[#2DC76D]">
                    {formatMembershipDuration(memberData.created_at)}
                  </div>
                </div>
                <div className="flex mb-3">
                  <div className="flex-[0_0_150px]">ระดับสมาชิก</div>
                  <div>
                    <span className="rounded-full bg-[#0760FC] px-3 text-white">
                      {memberData.memberGroup?.name || "-"}
                    </span>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-[0_0_150px]">คะแนนสะสม</div>
                  <div>
                    <span className="text-[#2DC76D]">
                      {formatCurrency(memberData.totalPoint || 0)}
                    </span>{" "}
                    คะแนน
                  </div>
                </div>
              </div>

              <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
                <div className="font-bold text-[#636578] text-xl mb-3">
                  ยอดหนี้สะสม
                </div>
                <div className="mb-2 text-2xl font-bold">
                  <span className="text-[#F95057] mr-3">
                    {formatCurrency(memberData.total_debt || 0)}
                  </span>
                  <span className="text-[#636578]">บาท</span>
                </div>
                <div className="text-[#636578]">ยอดใช้จ่ายสะสม</div>
              </div>

              <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
                <div className="font-bold text-[#636578] text-xl mb-3">
                  วงเงินคงเหลือที่ใช้ได้
                </div>
                <div className="mb-2 text-2xl font-bold">
                  <span className="text-[#565DFF] mr-3">
                    {formatCurrency(
                      Math.max(
                        (Number(memberData.credit_limit_amount) || 0) -
                          (Number(memberData.total_debt) || 0),
                        0
                      )
                    )}
                  </span>
                  <span className="text-[#636578]">บาท</span>
                </div>
                <div className="text-[#636578]">ยอดใช้จ่ายที่เป็นหนี้ได้</div>
              </div>

              <div className="col-span-2">
                <div className="font-bold text-[#636578] text-xl mb-3">
                  วงเงินทั้งหมด
                </div>
                <div className="mb-2 text-2xl font-bold">
                  <span className="text-[#2DC76D] mr-3">
                    {formatCurrency(memberData.credit_limit_amount || 0)}
                  </span>
                  <span className="text-[#636578]">บาท</span>
                </div>
                <div className="text-[#636578]">
                  ยอดใช้จ่ายที่เป็นหนี้ได้ทั้งหมด
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-6">
        {TAB_CONFIG.map((tab) => (
          <Button
            key={tab.id}
            variant={tabStatus === tab.id ? "contained" : ""}
            color="primary"
            onClick={() => handleChangeTab(tab.id)}
            className="mr-2"
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {tabStatus === 1 && <DebtorRecordTab />}
      {tabStatus === 2 && <InvoiceTab />}
      {tabStatus === 3 && <ReceiptTab />}
    </div>
  );
}